import { immer } from 'zustand/middleware/immer'

import type { UserInfoResponse } from '~/clients/derived-api-types'
import env from '~/env'

import type { Slice } from '../store'

type State = {
    display_name: string | null
    username: string | null
    read_only: boolean
    issuer: UserInfoResponse['issuer'] | null
    planning_domain_uuids: string[]
    apiAccessToken: string | null
    dipsApiAccessToken: string | null
    dipsApiAccessTokenIsFromPKCE: boolean
    idTokenPKCE?: string
}

const initialState: State = {
    display_name: null,
    username: null,
    read_only: false,
    issuer: null,
    planning_domain_uuids: [],
    apiAccessToken: null,
    dipsApiAccessToken: null,
    dipsApiAccessTokenIsFromPKCE: false,
    idTokenPKCE: undefined,
}

type Actions = {
    actions: {
        setDipsApiAccessToken: (args: {
            dipsApiAccessToken: State['dipsApiAccessToken']
            dipsApiAccessTokenIsFromPKCE: State['dipsApiAccessTokenIsFromPKCE']
            idTokenPKCE: State['idTokenPKCE']
        }) => void
        setAuth: (args: {
            display_name?: string | null
            username?: string | null
            read_only?: boolean
            accessToken: string | null
            issuer?: UserInfoResponse['issuer']
            planning_domain_uuids?: string[]
        }) => void
    }
}

export type UserSlice = {
    user: State & Actions
}

export const createUserSlice: Slice<UserSlice> = immer(set => ({
    user: {
        ...initialState,
        actions: {
            setDipsApiAccessToken: ({ dipsApiAccessToken, dipsApiAccessTokenIsFromPKCE, idTokenPKCE }) => {
                set(state => {
                    state.user.dipsApiAccessToken = dipsApiAccessToken
                    state.user.dipsApiAccessTokenIsFromPKCE = dipsApiAccessTokenIsFromPKCE
                    state.user.idTokenPKCE = idTokenPKCE
                })
            },
            setAuth: ({ display_name, username, read_only, accessToken, issuer, planning_domain_uuids }) => {
                set(state => {
                    state.user.apiAccessToken = accessToken
                    state.user.display_name = display_name ?? state.user.display_name
                    state.user.username = username ?? state.user.username
                    state.user.read_only = read_only ?? state.user.read_only
                    state.user.issuer = issuer ?? state.user.issuer
                    state.user.planning_domain_uuids = planning_domain_uuids ?? state.user.planning_domain_uuids

                    if (!env.VITE_PKCE_AUTHORIZE_URL) {
                        state.user.dipsApiAccessToken = accessToken
                        state.user.dipsApiAccessTokenIsFromPKCE = !accessToken
                    }
                })
            },
        },
    },
}))

// Transitional: once there is a switcher, a user with several planning domains picks one.
/** The user's active planning domain becomes the allowed one if it's unique, but `null` when they have none or several. */
export function selectActivePlanningDomainUuid(state: { user: Pick<State, 'planning_domain_uuids'> }): string | null {
    const [uuid, ...others] = state.user.planning_domain_uuids
    return uuid !== undefined && others.length === 0 ? uuid : null
}
